import React from 'react';
import ReactSelect, {
  StylesConfig as ReactSelectStyles,
  Theme as ReactSelectTheme,
} from 'react-select';
import CreatableSelect from 'react-select/creatable';
import DropdownIndicator from './Components/DropDownIndicatorView';

export interface SelectOption<T> {
  value: T;
  label: string;
}

const defaultTheme: (theme: ReactSelectTheme) => ReactSelectTheme = theme => ({
  ...theme,
  spacing: {
    ...theme.spacing,
    controlHeight: 46,
  },
});

const defaultStyles: Partial<ReactSelectStyles<any, any>> = {
  container: provided => {
    return {
      ...provided,
      width: '100%',
    };
  },
  control: (provided, state) => {
    return {
      ...provided,
      boxShadow: state.isFocused ? '0px 0px 4px rgba(24, 160, 251, 0.25)' : 'none',
      borderColor: state.isFocused ? '#18A0FB' : '#CCCCCC',
      borderBottomRightRadius:
        state.selectProps.menuIsOpen && state.selectProps.menuPlacement === 'bottom' ? 0 : '4px',
      borderBottomLeftRadius:
        state.selectProps.menuIsOpen && state.selectProps.menuPlacement === 'bottom' ? 0 : '4px',
      padding: '5px 0 5px 8px',
      minHeight: 'auto',
      backgroundColor: state.isDisabled ? '#e5e5e5' : '#fff',
      '&:hover': {},
    };
  },
  menu: provided => ({
    ...provided,
    marginTop: 0,
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0,
    zIndex: 4,
  }),
  multiValue: (provided, { data: { isInvalid } }) => ({
    ...provided,
    background: isInvalid ? '#fadcdc' : '#ccc',
    borderRadius: '4px',
  }),
  multiValueLabel: (provided, { data: { isInvalid } }) => ({
    ...provided,
    color: isInvalid ? '#c23934' : '#444',
    fontSize: '16px',
  }),
  multiValueRemove: (provided, { data: { isInvalid } }) => ({
    ...provided,
    color: isInvalid ? '#c23934' : '#444',
    '&:hover': {
      background: 'transparent',
      cursor: 'pointer',
      color: isInvalid ? '#c23934' : '#000',
    },
  }),
  input: provided => ({
    ...provided,
    minWidth: '25px',
  }),
  option: (provided, state) => {
    const { isFocused } = state;

    const styles = {
      ...provided,
      backgroundColor: '#fff',
      color: '#444444',
      fontSize: '16px',
      transition: 'background 0.3s ease',
    };

    if (isFocused) {
      return {
        ...styles,
        backgroundColor: '#ecf2f9',
        '&:hover': {
          backgroundColor: '#ecf2f9',
        },
      };
    }

    return styles;
  },
  indicatorsContainer: provided => ({
    ...provided,
    padding: '0 8px',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  placeholder: provided => ({
    ...provided,
    color: '#6C727F !important',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
  }),
};

const Select: React.FC<{ [k: string]: any }> = props => {
  const { isCreate, components } = props;

  if (isCreate) {
    return (
      <CreatableSelect
        {...props}
        components={{ DropdownIndicator, ...(components || {}) }}
        theme={defaultTheme}
        // eslint-disable-next-line react/destructuring-assignment
        styles={{ ...defaultStyles, ...props.styles }}
      />
    );
  }

  return (
    <ReactSelect
      {...props}
      components={{ DropdownIndicator } as any}
      theme={defaultTheme}
      // eslint-disable-next-line react/destructuring-assignment
      styles={{ ...defaultStyles, ...props.styles }}
    />
  );
};

export default Select;
